/*
ペース。打っている最中に「今どのくらいか」を数字ではなく生き物で見せる。

寿司打が面白いのは、皿が流れていくのが見えるからで、KPM が出るからでは
ない。こちらも同じで、340 KPM と言われても速いのか分からないが、うさぎ
を追い抜いたのは分かる。

**ふくろうだけ正確さも要る。** 速さだけで最上位に行けるようにすると、
打ち間違いを無視して叩き続けるのが最適解になる。眼鏡をかけた鳥に
「速いだけでは乗せない」役をやってもらう。
*/

export type PaceId = "kame" | "usagi" | "fukurou";

export interface Pace {
  id: PaceId;
  /** 画面に出す名前 */
  label: string;
  /** apps/client/public 直下に置いてある */
  image: string;
  /** ここに乗るのに要る打鍵速度 */
  kpm: number;
  /** 併せて要る正確率。無い段は速さだけで乗れる */
  accuracy?: number;
  /** 追い抜いたときに 1 行だけ出す */
  caption: string;
}

/** 速い順に並べない。レーンの左から右の順に持つ */
export const PACES: readonly Pace[] = [
  {
    id: "kame",
    label: "かめ",
    image: "/yumesaku_kame.svg",
    kpm: 120,
    caption: "かめを追い抜いた",
  },
  {
    id: "usagi",
    label: "うさぎ",
    image: "/yumesaku_usagi.svg",
    kpm: 240,
    caption: "うさぎに並んだ",
  },
  {
    id: "fukurou",
    label: "ふくろう",
    image: "/yumesaku_fukurou.svg",
    kpm: 340,
    accuracy: 95,
    caption: "ふくろうに追いついた",
  },
];

/**
 * レーンの右端。ここを超えても目盛りは伸びない。
 *
 * ふくろうの 340 より少しだけ先に置く。ふくろうが右端にいると、
 * 追い抜いた瞬間に印が壁に当たって止まり、伸びしろが無いように見える
 */
export const PACE_LANE_MAX_KPM = 420;

/** レーン上の位置。0 が左端、1 が右端 */
export function paceLaneRatio(kpm: number): number {
  if (!Number.isFinite(kpm) || kpm <= 0) return 0;
  return Math.min(1, kpm / PACE_LANE_MAX_KPM);
}

/** その段に乗れているか */
export function hasReached(pace: Pace, kpm: number, accuracy: number): boolean {
  if (kpm < pace.kpm) return false;
  if (pace.accuracy !== undefined && accuracy < pace.accuracy) return false;
  return true;
}

/**
 * 今いちばん上の段。どれにも届いていなければ null。
 *
 * 上から見る。ふくろうの正確率で落ちた人は、うさぎには乗っている
 */
export function reachedPace(kpm: number, accuracy: number): Pace | null {
  for (let i = PACES.length - 1; i >= 0; i--) {
    if (hasReached(PACES[i], kpm, accuracy)) return PACES[i];
  }
  return null;
}

/*
1 語ぶんの持ち時間。

寿司打の皿と同じで、**流れ切ったら食べられない**。どの相手が運んでくるか
で速さが変わる。かめの背中に乗った語はゆっくり流れ、ふくろうの語はすぐ
右へ抜けていく。

長さぶんは一律に伸ばす。長い語だけ理不尽に取れないと、語の運で結果が
決まってしまう
*/

/** 打ち始めるまでの下駄。読んで指を置く時間 */
export const PACE_GRACE_MS = 1_400;

/** その相手が運ぶ語を、右端まで何ミリ秒で流すか */
export function wordDurationMs(pace: Pace, keystrokes: number): number {
  return PACE_GRACE_MS + (keystrokes * 60_000) / pace.kpm;
}

/*
次に出す相手。連続で取れているほど速い相手が来る。

**1 つ逃したら かめ に戻す。** 崩れたところから速い相手が続くと、
立て直せないまま終わって、もう一度やる気にならない
*/
export function paceForStreak(streak: number): Pace {
  if (streak >= 8) return PACES[2];
  if (streak >= 3) return PACES[1];
  return PACES[0];
}

/*
コース。

寿司打が「60秒 / 2分 / 3分」ではなく「お手軽 / お勧め / 高級」で選ばせて
いるのを踏む。長さは選ぶものではなく、選んだコースの結果として付いてくる。

**名前だけ変えても飾りにしかならない**ので、コースで流れの速さも変える。
上級は最初からうさぎ以上が運んでくるし、逃してもかめまでは落ちない。
*/
export interface Course {
  /*
  **practice は選べるコースではない。** 90 秒の重点練習が Course の形を
  借りているだけで、COURSES には入れていない。id を union に足すのは、
  courseForDuration が 1 つの型で両方を返すため
  */
  id: "beginner" | "intermediate" | "advanced" | "practice";
  label: string;
  durationMs: number;
  /**
   * 連続数への下駄。ここが実質そのコースの下限になる。
   *
   * 逃したときに戻るのもこの位置。上級で 1 つ逃しただけで
   * かめまで落ちると、コースを選んだ意味が無くなる
   */
  paceBias: number;
  /**
   * 参加料。ここを超えたら元が取れる。
   *
   * 上のコースほど 1 分あたりで要る速さが上がるようにしてある
   * (初級 200 / 中級 233 / 上級 267 KPM あたりが分かれ目)
   */
  entryTokens: number;
  caption: string;
}

export const COURSES: readonly Course[] = [
  {
    id: "beginner",
    label: "初級",
    durationMs: 60_000,
    paceBias: 0,
    entryTokens: 3_000,
    caption: "かめが単語を運んでくる。まず打ち切る感じをつかむ",
  },
  {
    id: "intermediate",
    label: "中級",
    durationMs: 120_000,
    paceBias: 3,
    entryTokens: 7_000,
    caption: "うさぎが短い句を運んでくる。手を止めると流れていく",
  },
  {
    id: "advanced",
    label: "上級",
    durationMs: 180_000,
    paceBias: 8,
    entryTokens: 12_000,
    caption: "ふくろうが文を運んでくる。順位表に載るのはこのコース",
  },
];

/**
 * 重点練習の 90 秒。**COURSES には入れない。**
 *
 * 入れると画面のコース釦が 4 つに増え、順位表も 90 秒の土俵を持つことに
 * なる。どちらも docs/typing/battle-measurement-spec.md の表に無い。
 *
 * paceBias は中級と同じ 3。苦手キーの語をゆっくり丁寧に打つ回なので、
 * 初級の速さだと手が余り、上級だと狙ったキーを味わう前に流れていく。
 * 参加料は取らない。**下手なほど払うことになる回**にしない
 */
export const PRACTICE_COURSE: Course = {
  id: "practice",
  label: "重点練習",
  durationMs: 90_000,
  paceBias: 3,
  entryTokens: 0,
  caption: "苦手なキーを含む語だけが流れてくる。止めてよい",
};

export function courseForDuration(durationMs: number): Course {
  if (durationMs === PRACTICE_COURSE.durationMs) return PRACTICE_COURSE;
  return COURSES.find((c) => c.durationMs === durationMs) ?? COURSES[0];
}

/*
レーンの座標。

コンベアの見た目と、爆速の判定は同じ数から出す。別々に持つと、
線の手前で打ち切ったのに爆速にならない回ができて、何が起きたのか
説明できなくなる。
*/

/** 左から入ってくる位置。いきなり現れないよう少し外から */
export const LANE_ENTER = -0.08;
/** 逃す線。ここに着いたらその語は無くなる */
export const LANE_EXIT = 1;
/** 爆速の線。ここを越える前に打ち切れたら爆速 */
export const LANE_BLAZING = 0.45;

/** 経過の割合 (0〜1) から、レーン上のどこに居るか */
export function lanePosition(progress: number): number {
  return LANE_ENTER + (LANE_EXIT - LANE_ENTER) * progress;
}

/**
 * 爆速で打ち切れたか。
 *
 * 「速い相手なら爆速も速い」ことに注意する。ふくろうの持ち時間は
 * かめより短いので、同じ 45% でも実際に要る速さは違う。**そこは
 * わざとそのままにする。** 速い相手に付いて行けたこと自体が爆速で、
 * 相手ごとに違う秒数を覚えさせる必要は無い
 */
export function isBlazing(progress: number): boolean {
  return lanePosition(progress) <= LANE_BLAZING;
}


/*
点の付け方。

寿司打が面白いのは「速かった」ではなく「元が取れた / 損した」で終わる
からで、KPM は最後まで出てこない。同じ形にする。

**見せる単位は円。** 最初はトークンにしていたが、結果を見た人に
「トークンって分からない」と言われた (2026-09-15)。寿司打と同じ円なら
「元が取れた」が説明なしで伝わる。欄の名前 (entryTokens / earnedTokens) は
API と DB に残っているので変えない。変えるのは画面と共有文の単位だけ。
*/

/** 1 打鍵ぶんの値打ち */
export const TOKENS_PER_KEYSTROKE = 15;

/** 爆速で打ち切ったときの割り増し */
export const BLAZING_BONUS = 0.5;

/**
 * 1 語ぶんの取り分。
 *
 * 逃した語は 0。取れた語だけが積まれる
 */
export function wordTokens(keystrokes: number, blazing: boolean): number {
  const base = keystrokes * TOKENS_PER_KEYSTROKE;
  return Math.round(blazing ? base * (1 + BLAZING_BONUS) : base);
}

export interface Payout {
  /** 集めたトークン */
  earned: number;
  /** 参加料 */
  entry: number;
  /** 差し引き。プラスなら元が取れている */
  balance: number;
  profited: boolean;
}

export function payout(earned: number, course: Course): Payout {
  const balance = earned - course.entryTokens;
  return {
    earned,
    entry: course.entryTokens,
    balance,
    profited: balance >= 0,
  };
}

/** 3,200 を「3,200」にする。桁が読めないと得したのか分からない */
export function formatTokens(value: number): string {
  return Math.abs(value).toLocaleString("ja-JP");
}

/** 画面と共有文に出す形。「3,200円」。単位を離すと数字だけが目に入る */
export function formatYen(value: number): string {
  return `${formatTokens(value)}円`;
}
